import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";

const blogPosts = [
  {
    id: 1,
    title: "10 Luxury Interior Design Trends for 2024",
    excerpt:
      "Discover the latest trends in luxury interior design that are reshaping modern homes.",
    image: "https://images.unsplash.com/photo-1758448755952-42b404bc6f39?w=600&q=80",
    date: "Dec 15, 2023",
    category: "Trends",
  },
  {
    id: 2,
    title: "Maximizing Space in Small Apartments",
    excerpt:
      "Expert tips on creating functional and beautiful interiors in compact living spaces.",
    image: "https://images.unsplash.com/photo-1648634158203-199accfd7afc?w=600&q=80",
    date: "Dec 10, 2023",
    category: "Design Tips",
  },
  {
    id: 3,
    title: "Sustainable Materials in Modern Interiors",
    excerpt:
      "How eco-friendly materials are transforming luxury interior design.",
    image: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?w=600&q=80",
    date: "Dec 5, 2023",
    category: "Sustainability",
  },
];

export const BlogSection = () => {
  return (
    <section className="section bg-background">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.3em] text-[11px] text-muted-foreground mb-4">
            Our Journal
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground">
            From Our Journal
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Dive into our inspiring blog for expert tips, trends, and transformative
            ideas in interior design.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="blog-grid">
          {blogPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid="blog-card"
            >
              <Card className="overflow-hidden border-[hsl(var(--border))] hover:shadow-[0_12px_40px_rgb(0_0_0/0.18)] transition-shadow h-full">
                <div className="aspect-[16/10] relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                    <span className="px-3 py-1 rounded-full bg-[hsl(var(--brand-gold))]/10 text-[hsl(var(--brand-gold))]">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                  </div>
                  <h3 className="font-serif-alt text-xl font-semibold text-foreground">
                    {post.title}
                  </h3>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <Button variant="link" className="p-0 h-auto text-[hsl(var(--brand-gold))] hover:text-[hsl(var(--brand-gold))]/80">
                    Read More <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
